import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Feature, FeatureStore, FeatureStoreSelectionStrategy } from '@igo2/geo';
import { Fin, LocalisationDroit, MrcDroit, MunicipaliteDroit, RegionAdministrativeDroit, StatutDroit, CodeTypeDroit } from 'src/app/shared/models/droit-gdf';
import { RechercheGdfService } from 'src/app/shared/services/recherche-gdf.service';
import { Constantes } from 'src/app/shared/constantes';
import { SIGTRechercheGdfBaseComponent } from '../_sigt-recherche-gdf.base.component';
import { and, equalTo, or } from 'ol/format/filter';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { EntityRecord, EntityTableButton, EntityTableColumnRenderer, EntityTableTemplate } from '@igo2/common/entity';
import { LanguageService } from '@igo2/core/language';
import { MessageService } from '@igo2/core/message';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FilterableMatSelectDirective } from 'src/app/shared/directives/filterable-mat-select.directive';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import * as olstyle from 'ol/style';
import Filter from 'ol/format/filter/Filter';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

export type EntityKey = string | number;

@Component({
    selector: 'app-droit-gdf',
    templateUrl: './droit-gdf.component.html',
    styleUrls: ['./droit-gdf.component.scss'],
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        FilterableMatSelectDirective
    ]
})
export class DroitGdfComponent extends SIGTRechercheGdfBaseComponent implements OnInit {
  propertyKeyAndTitle: { [key: string]: string };
  template: EntityTableTemplate;

  form: UntypedFormGroup;
  public codeTypeDroitListeDeroulante: CodeTypeDroit[] = [];
  public finsListeDeroulante: Fin[] = [];
  public statutListeDeroulante: StatutDroit[] = [];
  public listeLocalisations: any[] = [];
  public regionsAdministrativeListeDeroulante: RegionAdministrativeDroit[] = [];
  public mrcListeDeroulante: MrcDroit[] = [];
  public municipaliteListeDeroulante: MunicipaliteDroit[] = [];
  #configService = inject(ZzzConfigService);
  #valeurStatutDefaut: string = 'A';

  get urlBaseApi(): string {
    return this.#configService.get().authApiEndPoint + "/sigtapi";
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    languageService: LanguageService,
    messageService: MessageService,
    rechercherGdfService: RechercheGdfService,
    httpClient: HttpClient,
    cdRef: ChangeDetectorRef,
  ) {
    super(rechercherGdfService, httpClient, messageService, languageService, cdRef);
    this.propertyKeyAndTitle = this.rechercherGdfService.getPropertyKeyAndTitle('droitGdf');
    this.template = this.rechercherGdfService.getEntityTableTemplate(
      'droitGdf',
        (row, record) => {
          if (record?.state?.selected) {
            const olFeature = record?.entity.ol;
            const store:FeatureStore = olFeature?.get("_featureStore");
            const featureStoreSelectionStrategy = store.getStrategyOfType(FeatureStoreSelectionStrategy) as FeatureStoreSelectionStrategy;
            const layer = featureStoreSelectionStrategy?.overlayStore.layer;
            this.rechercherGdfService.selectLegendAndPrintMap(row, layer);
          }
        }
      );
  }

  ngOnInit(): void {
    this.style = new olstyle.Style({
      stroke: new olstyle.Stroke({
        width: 1,
        color: [0, 0, 0, 1]
      }),
      fill: new olstyle.Fill({
        color: [56, 168, 0, 1]
      }),
      image: new olstyle.Circle({
        radius: 2,
        stroke: new olstyle.Stroke({
          width: 1,
          color: [0, 0, 0, 1]
        }),
        fill: new olstyle.Fill({
          color: [56, 168, 0, 1]
        })
      })
    });
    this.getLayer("Droit");
    this.form = this.formBuilder.group({
      codeTypeDroit: ["", [Validators.required]],
      fin: [""],
      statutDroit: [""],
      municipalite: [""],
      mrc: [""],
      regionAdministrative: [""]
    }, { validators: [this.validerLocalisation.bind(this)] });

    this.messageApplication.grilleVide = Constantes.MessageApplicatif.AucunElement_6I;
    this.messageApplication.erreurCouche = Constantes.MessageApplicatif.CoucheIndisponible_16T;
    this.messageApplication.obligatoireTypeDroit = Constantes.MessageApplicatif.ValeurObligatoire_1E
      .replace("{0}", this.languageService.translate.instant('sigt.integration.rechercheGdf.form.droitGdf.codeTypeDroit'));

    this.chargerTypesDroits();
    this.chargerStatus();
  }

  chargerTypesDroits() {
    this.httpClient.get<{ listeCodeTypeDroit: CodeTypeDroit[] }>(this.urlBaseApi + "/gdf/droit/types-droit"
    ).subscribe({
      next: reponse => {
        this.codeTypeDroitListeDeroulante = reponse.listeCodeTypeDroit.sort((a, b) =>
          a.descriptionTypeDroit.localeCompare(b.descriptionTypeDroit));
      },
      error: error => {
        this.messageService.error("ERREUR APPEL gdf/droit/types-droit");
        console.log(error);
      }
    });
  }

  chargerStatus() {
    //Les status sont chargés une seule fois
    this.httpClient.get<{ listeStatutDroit: StatutDroit[] }>(this.urlBaseApi + "/gdf/droit/statuts")
      .subscribe({
        next: reponse => {
          this.statutListeDeroulante = reponse.listeStatutDroit.sort((a, b) => a.nomStatut.localeCompare(b.nomStatut));
          this.defautStatut();
        },
        error: error => {
          this.messageService.error("ERREUR APPEL gdf/droit/statuts");
          console.log(error);
        }
      });
  }

  chargerLocalisations(selectedTypeDroit: string[], selectedFins: string[], selectedStatus: string[]) {
    this.httpClient.post<{ listeLocalisations: LocalisationDroit[] }>(this.urlBaseApi + "/gdf/droit/localisations",
      {
        "criteresTypeDroit": selectedTypeDroit,
        "criteresFin": selectedFins,
        "criteresStatut": selectedStatus
      }
    ).subscribe({
      next: reponse => {
        this.listeLocalisations = reponse.listeLocalisations;

        let distinctRegions = reponse.listeLocalisations.reduce((prev, el) => {
          if (prev.some(o => o.codeRegion === el.codeRegion && o.nomRegion === el.nomRegion))
            return prev;
          return [...prev, { codeRegion: el.codeRegion, nomRegion: el.nomRegion }];
        }, []
        ).sort((a, b) => Number(a.codeRegion) - Number(b.codeRegion));

        distinctRegions = distinctRegions.map(({ codeRegion, nomRegion }) => (
          {
            codeRegion,
            nomRegion: (codeRegion.length === 2 ? codeRegion : "0" + codeRegion) + ' - ' + nomRegion
          }
        ));
        this.regionsAdministrativeListeDeroulante = distinctRegions;

        this.mrcListeDeroulante = reponse.listeLocalisations
        .filter(el => el.codeMrc !== '-') //SIGT-1568        
        .reduce((prev, el) => {
          if (prev.some(o => o.codeMrc === el.codeMrc && o.nomMrc === el.nomMrc))
            return prev;
          return [...prev, { codeMrc: el.codeMrc, nomMrc: el.nomMrc }];
        }, []
        ).sort((a, b) => a.nomMrc.localeCompare(b.nomMrc));

        this.municipaliteListeDeroulante = reponse.listeLocalisations
          .map(({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
          .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));

        let selectionRegionsEncoreValide;
        if (this.form.get("regionAdministrative").value) {
          selectionRegionsEncoreValide = Object.values(this.form.get("regionAdministrative").value).filter(x => {
            if (this.regionsAdministrativeListeDeroulante.some(item => item.codeRegion === x))
              return x;
          });
        }
        this.form.get("regionAdministrative").setValue(selectionRegionsEncoreValide);

        let selectionMrcEncoreValide;
        if (this.form.get("mrc").value) {
          selectionMrcEncoreValide = Object.values(this.form.get("mrc").value).filter(x => {
            if (this.mrcListeDeroulante.some(item => item.codeMrc === x))
              return x;
          });
        }
        this.form.get("mrc").setValue(selectionMrcEncoreValide);

        let selectionMuniEncoreValide;
        if (this.form.get("municipalite").value) {
          selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
            if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x))
              return x;
          });
        }
        this.form.get("municipalite").setValue(selectionMuniEncoreValide);
      },
      error: error => {
        this.messageService.error("ERREUR APPEL gdf/droit/localisations");
        console.log(error);
      }
    });
  }

  selectTypeDroitChanged() {
    this.httpClient.post<{ listeFins: Fin[] }>(this.urlBaseApi + "/gdf/droit/fins",
      {
        "criteresTypeDroit": this.form.get("codeTypeDroit").value
      }).subscribe({
        next: reponse => {
          let selectionFinEncoreValide;
          if (this.form.get("fin").value) {
            selectionFinEncoreValide = Object.values(this.form.get("fin").value).filter(x => {
              if (reponse.listeFins.some(item => item.codeFin === x))
                return x;
            });
          }
          let finsAvecNo = reponse.listeFins.map(item => ({
            codeFin: item.codeFin,
            descriptionFin: item.codeFin + ' - ' + item.descriptionFin
          }));
          this.finsListeDeroulante = finsAvecNo.sort((a, b) => Number(a.codeFin) - Number(b.codeFin));
          this.form.get("fin").setValue(selectionFinEncoreValide);
          this.chargerLocalisations(this.form.get("codeTypeDroit").value, selectionFinEncoreValide, this.form.get("statutDroit").value);
        },
        error: error => {
          this.messageService.error("ERREUR APPEL gdf/droit/fins");
          console.log(error);
        }
      });
  }

  selectFinChanged() {
    this.chargerLocalisations(this.form.get("codeTypeDroit").value, this.form.get("fin").value, this.form.get("statutDroit").value);
  }

  selectStatutChanged() {
    this.chargerLocalisations(this.form.get("codeTypeDroit").value, this.form.get("fin").value, this.form.get("statutDroit").value);
  }

  selectMrcChange() {
    let localisationFiltrer = this.listeLocalisations;
    if (this.form.get("mrc").value && (Object.values(this.form.get("mrc").value)).length > 0) {
      localisationFiltrer = this.listeLocalisations.filter(x => {
        if ((Object.values(this.form.get("mrc").value)).some(item => item === x.codeMrc))
          return x;
      });
    }

    if (this.form.get("regionAdministrative").value && (Object.values(this.form.get("regionAdministrative").value)).length > 0) {
      localisationFiltrer = localisationFiltrer.filter(x => {
        if ((Object.values(this.form.get("regionAdministrative").value)).some(item => item === x.codeRegion))
          return x;
      });
    }

    this.municipaliteListeDeroulante = localisationFiltrer.map(
      ({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
      .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));

    let selectionMuniEncoreValide;
    if (this.form.get("municipalite").value) {
      selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
        if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x))
          return x;
      });
    }
    this.form.get("municipalite").setValue(selectionMuniEncoreValide);
  }

  selectRegionChange() {
    let localisationFiltrerRegion = this.listeLocalisations;
    if (this.form.get("regionAdministrative").value && (Object.values(this.form.get("regionAdministrative").value)).length > 0) {
      localisationFiltrerRegion = this.listeLocalisations.filter(x => {
        if ((Object.values(this.form.get("regionAdministrative").value)).some(item => item === x.codeRegion))
          return x;
      });
    }
    this.mrcListeDeroulante = localisationFiltrerRegion
    .filter(el => el.codeMrc !== '-') //SIGT-1568    
    .reduce((prev, el) => {
      if (prev.some(o => o.codeMrc === el.codeMrc && o.nomMrc === el.nomMrc))
        return prev;
      return [...prev, { codeMrc: el.codeMrc, nomMrc: el.nomMrc }];
    }, []
    ).sort((a, b) => a.nomMrc.localeCompare(b.nomMrc));

    if (this.form.get("mrc").value && (Object.values(this.form.get("mrc").value)).length > 0) {
      let selectionMrcEncoreValide;
      selectionMrcEncoreValide = Object.values(this.form.get("mrc").value).filter(x => {
        if (this.mrcListeDeroulante.some(item => item.codeMrc === x)) return x;
      });
      this.form.get("mrc").setValue(selectionMrcEncoreValide);
      this.selectMrcChange();
    }
    else {
      this.municipaliteListeDeroulante = localisationFiltrerRegion.map(
        ({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
        .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));
      let selectionMuniEncoreValide;
      if (this.form.get("municipalite").value) {
        selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
          if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x)) return x;
        });
      }
      this.form.get("municipalite").setValue(selectionMuniEncoreValide);
    }
  }

  initialiserChamps(): void {
    this.form.setValue({
      codeTypeDroit: '',
      fin: '',
      statutDroit: '',
      municipalite: '',
      mrc: '',
      regionAdministrative: ''
    });
    this.finsListeDeroulante = [];
    this.defautStatut();
    this.municipaliteListeDeroulante = [];
    this.mrcListeDeroulante = [];
    this.regionsAdministrativeListeDeroulante = [];
    this.form.get('codeTypeDroit').setErrors(null, { emitEvent: false });
  }

  defautStatut() {
    if (this.form.get('statutDroit').value == null || String(this.form.get('statutDroit').value) === '') {
      if (this.statutListeDeroulante.filter(statut => statut.codeStatut === this.#valeurStatutDefaut).length > 0) {
        this.form.get("statutDroit").setValue([this.#valeurStatutDefaut]);
      }
    }
  }

  buildFilter(): Filter {
    let filter: Filter;
    let subFilters: Filter[] = [];

    var codeTypeDroit = this.form.get("codeTypeDroit").value;
    var fin = this.form.get("fin").value;
    var statut = this.form.get("statutDroit").value;
    var municipalite = this.form.get("municipalite").value;
    var mrc = this.form.get("mrc").value;
    var regionAdministrative = this.form.get("regionAdministrative").value;

    {
      let eqFilters = codeTypeDroit.map(t => equalTo('TDR_CODE_DROIT', t));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    if (fin !== undefined && fin.length > 0) {
      let eqFilters = fin.map(f => equalTo('FIN_NO', Number(f)));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    if (statut !== undefined && statut.length > 0) {
      let eqFilters = statut.map(f => equalTo('DRT_STATU', f));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }

    if (municipalite !== undefined && municipalite.length > 0) {
      let eqFilters = municipalite.map(m => equalTo('MUN_NO', m));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    else if (mrc !== undefined && mrc.length > 0) {
      let eqFilters = mrc.map(m => equalTo('MRC_NO', m));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    else if (regionAdministrative !== undefined && regionAdministrative.length > 0) {
      let eqFilters = regionAdministrative.map(r => equalTo('REA_NO', r));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }

    filter = subFilters.length > 1 ? and(...subFilters) : subFilters[0];

    return filter;
  }

  formaterChampsFeaturePourResultatRecherche(features: Feature[]): Feature[] {
    return this.rechercherGdfService.formatFeaturesForTable('droitGdf', features);
  }

  validerLocalisation(control: FormGroup): any {
    const municipalite = control.controls["municipalite"].value;
    const mrc = control.controls["mrc"].value;
    const regionAdministrative = control.controls["regionAdministrative"].value;

    if ((municipalite?.length > 0 || mrc?.length > 0 || regionAdministrative?.length > 0)) {
      return null;
    }
    return { notValid: true };
  }
}
